/**
 * @author mschuetz / http://mschuetz.at
 *
 * adapted from THREE.OrbitControls by
 *
 * @author qiao / https://github.com/qiao
 * @author mrdoob / http://mrdoob.com
 * @author alteredq / http://alteredqualia.com/
 * @author WestLangley / http://github.com/WestLangley
 * @author erich666 / http://erichaines.com
 *
 *
 *
 */

import * as THREE from 'three';
import { MOUSE, CameraMode } from '../defines.js';
import { Utils } from '../utils.js';
import { EventDispatcher } from '../EventDispatcher.js';

// Guards against crossing through the orbit pivot (radius <= 0) on a large frame step.
const SAFETY_MIN_RADIUS = 1e-6;

export class OrbitControls extends EventDispatcher {
    constructor(viewer) {
        super();
        this.cesiumViewer = 'cesiumViewer';

        this.viewer = viewer;
        this.renderer = viewer.renderer;

        this.scene = null;
        this.sceneControls = new THREE.Scene();

        this.rotationSpeed = 5;

        this.fadeFactor = 20;
        this.yawDelta = 0;
        this.pitchDelta = 0;
        this.panDelta = new THREE.Vector2(0, 0);
        this.radiusDelta = 0;

        this.doubleClockZoomEnabled = true;

        this.tweens = [];

        // Flags for inversion
        this.invertYaw = false;
        this.invertPitch = false;

        this.twoDimensional = false;
        this.lockZoom = false;

        this.zoomToMap = (mouse) => {
            let camera = this.scene.getActiveCamera();

            const nmouse = {
                x: (mouse.x / this.renderer.domElement.clientWidth) * 2 - 1,
                y: -(mouse.y / this.renderer.domElement.clientHeight) * 2 + 1,
            };

            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(nmouse, camera);
            raycaster.firstHitOnly = true;

            const tilesScene = this.viewer.getTilesScene();
            if (!tilesScene) return;

            const intersection = raycaster.intersectObjects(tilesScene.children, true);
            if (!intersection || intersection.length === 0) return;
            this.dispatchHideMeshes();
            const I = intersection[0];
            let minimumJumpDistance = 20;

            let d = this.scene.view.direction.multiplyScalar(-1);
            let cameraTargetPosition = new THREE.Vector3().addVectors(I.point, d.multiplyScalar(minimumJumpDistance));
            let animationDuration = 600;
            let easing = TWEEN.Easing.Quartic.Out;

            let value = { x: 0 };
            let tween = new TWEEN.Tween(value).to({ x: 1 }, animationDuration);
            tween.easing(easing);
            this.tweens.push(tween);

            let startPos = this.scene.view.position.clone();
            let targetPos = cameraTargetPosition.clone();
            let startRadius = this.scene.view.radius;
            let targetRadius = cameraTargetPosition.distanceTo(I.point);

            tween.onUpdate(() => {
                let t = value.x;
                this.scene.view.position.lerpVectors(startPos, targetPos, t);
                this.scene.view.radius = (1 - t) * startRadius + t * targetRadius;
                this.viewer.setMoveSpeed(this.scene.view.radius);
            });

            tween.onComplete(() => {
                this.tweens = this.tweens.filter((e) => e !== tween);
                this.dispatchShowMeshes();
            });

            tween.start();
        };

        this.drag = (e) => {
            if (e.drag.object !== null) {
                return;
            }

            if (e.drag.startHandled === undefined) {
                e.drag.startHandled = true;

                this.dispatchEvent({ type: 'start' });
                this.dispatchHideMeshes();
            }

            let ndrag = {
                x: e.drag.lastDrag.x / this.renderer.domElement.clientWidth,
                y: e.drag.lastDrag.y / this.renderer.domElement.clientHeight,
            };

            if (e.drag.mouse === MOUSE.LEFT && !this.twoDimensional) {
                // === apply inversion multipliers ===
                let yawMult = this.invertYaw ? -1 : 1;
                let pitchMult = this.invertPitch ? -1 : 1;
                this.yawDelta += yawMult * ndrag.x * this.rotationSpeed;
                this.pitchDelta += pitchMult * ndrag.y * this.rotationSpeed;

                this.stopTweens();
            } else if (e.drag.mouse === MOUSE.RIGHT) {
                this.panDelta.x += ndrag.x;
                this.panDelta.y += ndrag.y;

                this.stopTweens();
            }
        };

        this.drop = (e) => {
            this.dispatchEvent({ type: 'end' });
            this.dispatchShowMeshes();
        };

        this.toggleTopView = () => {
            this.stopTweens();
            const view = this.scene.view;
            const pivot = view.getPivot().clone();
            const startPos = view.position.clone();
            const targetPos = pivot.clone().setZ(pivot.z + view.radius);
            const startPitch = view.pitch;
            const targetPitch = -Math.PI / 2;
            const duration = 600;
            const easing = TWEEN.Easing.Quartic.Out;
            let obj = { t: 0 };
            let tween = new TWEEN.Tween(obj)
                .to({ t: 1 }, duration)
                .easing(easing)
                .onUpdate(() => {
                    view.pitch = (1 - obj.t) * startPitch + obj.t * targetPitch;
                    view.position.lerpVectors(startPos, targetPos, obj.t);
                })
                .onComplete(() => {
                    view.pitch = targetPitch;
                    view.position.copy(targetPos);
                    view.direction = new THREE.Vector3(0, 0, -1);
                    this.tweens = this.tweens.filter((e) => e !== tween);
                    this.dispatchShowMeshes();
                });
            this.tweens.push(tween);
            tween.start();
        };

        this.scroll = (e) => {
            if (this.lockZoom && this.twoDimensional) return;

            // Clear existing scroll end timeout
            if (this.scrollEndTimeout) {
                clearTimeout(this.scrollEndTimeout);
            }

            // Standard THREE.js-style scrolling behavior
            let resolvedRadius = this.scene.view.radius + this.radiusDelta;

            // Boost the zoom step past the UX floor so perspective walkthroughs keep gliding instead
            // of stalling; orthographic gets no boost since it stops at the floor anyway.
            const PAST_MIN_RADIUS_SPEED_MULTIPLIER = 5;
            const pastFloor = resolvedRadius <= this.getMinRadius();
            const speedMultiplier = pastFloor && this.scene.cameraMode !== CameraMode.ORTHOGRAPHIC ? PAST_MIN_RADIUS_SPEED_MULTIPLIER : 1;

            this.radiusDelta += -e.delta * resolvedRadius * 0.1 * speedMultiplier;

            this.scene.view.radius = Math.max(this.scene.view.radius, this.getMinRadius());
            this.stopTweens();

            // Dispatch scroll_end event after scrolling stops (150ms debounce)
            this.scrollEndTimeout = setTimeout(() => {
                this.dispatchEvent({ type: 'scroll_end' });
            }, 150);
        };

        this.dblclick = (e) => {
            if (this.doubleClockZoomEnabled) {
                this.zoomToLocation(e.mouse);
            }
        };

        //double tap detection
        let lastTap = 0;
        const doubleTapThreshold = 300;

        this.handleDoubleTap = (e) => {
            const currentTime = new Date().getTime();
            const timeDifference = currentTime - lastTap;

            if (timeDifference < doubleTapThreshold && timeDifference > 0) {
                const rect = viewer.renderer.domElement.getBoundingClientRect();
                let x = e.changedTouches[0].pageX - rect.left;
                let y = e.changedTouches[0].pageY - rect.top;
                let mouse = { x, y };
                this.zoomToLocation(mouse);
            }

            lastTap = currentTime;
        };

        this.previousTouch = null;
        this.touchStart = (e) => {
            this.previousTouch = e;
        };

        this.touchEnd = (e) => {
            this.previousTouch = e;
        };

        this.touchMove = (e) => {
            this.dispatchHideMeshes();
            if (e.touches.length === 2 && this.previousTouch.touches.length === 2) {
                let prev = this.previousTouch;
                let curr = e;

                let prevDX = prev.touches[0].pageX - prev.touches[1].pageX;
                let prevDY = prev.touches[0].pageY - prev.touches[1].pageY;
                let prevDist = Math.sqrt(prevDX * prevDX + prevDY * prevDY);

                let currDX = curr.touches[0].pageX - curr.touches[1].pageX;
                let currDY = curr.touches[0].pageY - curr.touches[1].pageY;
                let currDist = Math.sqrt(currDX * currDX + currDY * currDY);

                let delta = currDist / prevDist;
                let resolvedRadius = this.scene.view.radius + this.radiusDelta;
                let newRadius = resolvedRadius / delta;
                this.radiusDelta = newRadius - resolvedRadius;

                this.stopTweens();
            } else if (e.touches.length === 3 && this.previousTouch.touches.length === 3) {
                let prev = this.previousTouch;
                let curr = e;

                let prevMeanX = (prev.touches[0].pageX + prev.touches[1].pageX + prev.touches[2].pageX) / 3;
                let prevMeanY = (prev.touches[0].pageY + prev.touches[1].pageY + prev.touches[2].pageY) / 3;

                let currMeanX = (curr.touches[0].pageX + curr.touches[1].pageX + curr.touches[2].pageX) / 3;
                let currMeanY = (curr.touches[0].pageY + curr.touches[1].pageY + curr.touches[2].pageY) / 3;

                let delta = {
                    x: (currMeanX - prevMeanX) / this.renderer.domElement.clientWidth,
                    y: (currMeanY - prevMeanY) / this.renderer.domElement.clientHeight,
                };

                this.panDelta.x += delta.x;
                this.panDelta.y += delta.y;

                this.stopTweens();
            }
            this.dispatchShowMeshes();
            this.previousTouch = e;
        };

        this.addEventListener('touchstart', this.touchStart);
        this.addEventListener('touchend', this.touchEnd);
        this.addEventListener('touchmove', this.touchMove);
        this.addEventListener('drag', this.drag);
        this.addEventListener('drop', this.drop);
        this.addEventListener('mousewheel', this.scroll);
        this.addEventListener('dblclick', this.dblclick);
        this.addEventListener('touchstart', this.handleDoubleTap);
    }

    dispatchHideMeshes() {
        this.dispatchEvent({ type: 'hideMeshes' });
    }

    dispatchShowMeshes() {
        this.dispatchEvent({ type: 'showMeshes' });
    }

    removeDragEvent() {
        this.removeEventListener('drag', this.drag);
    }

    addDragEvent() {
        this.addEventListener('drag', this.drag);
    }

    setScene(scene) {
        this.scene = scene;
    }

    stop() {
        this.yawDelta = 0;
        this.pitchDelta = 0;
        this.radiusDelta = 0;
        this.panDelta.set(0, 0);
    }

    zoomToLocation(mouse) {
        let camera = this.scene.getActiveCamera();

        let I = Utils.getMousePointCloudIntersection(mouse, camera, this.viewer, this.scene.pointclouds, { pickClipped: true });

        if (I === null) {
            return;
        }
        this.dispatchHideMeshes();

        let targetRadius = 0;
        {
            let minimumJumpDistance = 0.2;

            let domElement = this.renderer.domElement;
            let ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);

            let nodes = I.pointcloud.nodesOnRay(I.pointcloud.visibleNodes, ray);
            let lastNode = nodes[nodes.length - 1];
            let radius = lastNode.getBoundingSphere(new THREE.Sphere()).radius;
            targetRadius = Math.min(this.scene.view.radius, radius);
            targetRadius = Math.max(minimumJumpDistance, targetRadius);
        }

        let d = this.scene.view.direction.multiplyScalar(-1);
        let cameraTargetPosition = new THREE.Vector3().addVectors(I.location, d.multiplyScalar(targetRadius));
        // TODO Unused: let controlsTargetPosition = I.location;

        let animationDuration = 600;
        let easing = TWEEN.Easing.Quartic.Out;

        {
            // animate
            let value = { x: 0 };
            let tween = new TWEEN.Tween(value).to({ x: 1 }, animationDuration);
            tween.easing(easing);
            this.tweens.push(tween);

            let startPos = this.scene.view.position.clone();
            let targetPos = cameraTargetPosition.clone();
            let startRadius = this.scene.view.radius;
            let targetRadius = cameraTargetPosition.distanceTo(I.location);

            tween.onUpdate(() => {
                let t = value.x;
                this.scene.view.position.x = (1 - t) * startPos.x + t * targetPos.x;
                this.scene.view.position.y = (1 - t) * startPos.y + t * targetPos.y;
                this.scene.view.position.z = (1 - t) * startPos.z + t * targetPos.z;

                this.scene.view.radius = (1 - t) * startRadius + t * targetRadius;
                this.viewer.setMoveSpeed(this.scene.view.radius);
            });

            tween.onComplete(() => {
                this.tweens = this.tweens.filter((e) => e !== tween);
                this.dispatchShowMeshes();
            });

            tween.start();
        }
    }

    zoomToPoint(targetPoint) {
        let camera = this.scene.getActiveCamera();
        this.dispatchHideMeshes();

        let targetRadius = 2.3;
        let d = this.scene.view.direction.clone().multiplyScalar(-1);
        let cameraTargetPosition = new THREE.Vector3().addVectors(targetPoint, d.multiplyScalar(targetRadius));

        let animationDuration = 600;
        let easing = TWEEN.Easing.Quartic.Out;

        let value = { x: 0 };
        let tween = new TWEEN.Tween(value).to({ x: 1 }, animationDuration);
        tween.easing(easing);
        this.tweens.push(tween);

        let startPos = this.scene.view.position.clone();
        let targetPos = cameraTargetPosition.clone();
        let startRadius = this.scene.view.radius;
        let finalRadius = cameraTargetPosition.distanceTo(targetPoint);

        tween.onUpdate(() => {
            let t = value.x;
            this.scene.view.position.x = (1 - t) * startPos.x + t * targetPos.x;
            this.scene.view.position.y = (1 - t) * startPos.y + t * targetPos.y;
            this.scene.view.position.z = (1 - t) * startPos.z + t * targetPos.z;

            this.scene.view.radius = (1 - t) * startRadius + t * finalRadius;
            this.viewer.setMoveSpeed(this.scene.view.radius);
        });

        tween.onComplete(() => {
            this.tweens = this.tweens.filter((e) => e !== tween);
            this.dispatchShowMeshes();
        });

        tween.start();
    }

    stopTweens() {
        this.tweens.forEach((e) => e.stop());
        this.tweens = [];
    }

    getMinRadius() {
        const MIN_RADIUS_RATIO = 0.02;
        const MIN_RADIUS_CEILING = 2.5;
        const ABSOLUTE_MIN_RADIUS = 1e-4;

        const contentRadius = this.viewer.getSceneRadius();
        return contentRadius
            ? Math.min(MIN_RADIUS_CEILING, Math.max(contentRadius * MIN_RADIUS_RATIO, ABSOLUTE_MIN_RADIUS))
            : MIN_RADIUS_CEILING;
    }

    update(delta) {
        let view = this.scene.view;

        {
            // apply rotation
            let progression = Math.min(1, this.fadeFactor * delta);

            let yaw = view.yaw;
            let pitch = view.pitch;
            let pivot = view.getPivot();

            yaw -= progression * this.yawDelta;
            pitch -= progression * this.pitchDelta;

            view.yaw = yaw;
            view.pitch = pitch;

            let V = this.scene.view.direction.multiplyScalar(-view.radius);
            let position = new THREE.Vector3().addVectors(pivot, V);

            view.position.copy(position);
        }

        {
            // apply pan
            let progression = Math.min(1, this.fadeFactor * delta);
            // Pan speed is radius-proportional, so deep in a perspective walkthrough (radius can glide
            // to near zero) it would stall; floor it well above the (already tight) zoom floor so a
            // pan still feels usable right after passing through an element.
            const PAN_MIN_RADIUS_MULTIPLIER = 5;
            let panDistance = progression * Math.max(view.radius, this.getMinRadius() * PAN_MIN_RADIUS_MULTIPLIER) * 3;

            let px = -this.panDelta.x * panDistance;
            let py = this.panDelta.y * panDistance;

            view.pan(px, py);
        }

        {
            // apply zoom
            let progression = Math.min(1, this.fadeFactor * delta);

            // let radius = view.radius + progression * this.radiusDelta * view.radius * 0.1;
            let radius = view.radius + progression * this.radiusDelta;
            if (this.scene.cameraMode === CameraMode.ORTHOGRAPHIC) {
                // Enforce the floor every frame to stop the frustum scale from collapsing and
                // snapping back (the zoom "bounce"); clamp to the current radius if already closer.
                radius = Math.max(radius, Math.min(view.radius, this.getMinRadius()));
            } else {
                // Perspective can glide through the pivot for the walkthrough; just guard against zero/negative.
                radius = Math.max(radius, SAFETY_MIN_RADIUS);
            }

            let V = view.direction.multiplyScalar(-radius);
            let position = new THREE.Vector3().addVectors(view.getPivot(), V);
            view.radius = radius;

            view.position.copy(position);
        }

        {
            let speed = view.radius;
            this.viewer.setMoveSpeed(speed);
        }

        {
            // decelerate over time
            let progression = Math.min(1, this.fadeFactor * delta);
            let attenuation = Math.max(0, 1 - this.fadeFactor * delta);

            this.yawDelta *= attenuation;
            this.pitchDelta *= attenuation;
            this.panDelta.multiplyScalar(attenuation);
            // this.radiusDelta *= attenuation;
            this.radiusDelta -= progression * this.radiusDelta;
        }
    }
}
